import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Text,
  Select,
  Input,
  Checkbox,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { TaskFormData, ExtractionConfig } from '@/lib/sdk/chatbot/types';

interface ExtractionConfigSectionProps {
  control: Control<TaskFormData>;
  setValue: UseFormSetValue<TaskFormData>;
}

// Common field names per parser, offered as checkboxes so authors don't have to
// remember/guess the extractor's own vocabulary (e.g. "dob" vs "date_of_birth").
// Not exhaustive — the free-text input below still accepts anything else, since
// "document" in particular has no fixed vocabulary (it's driven by the task's own
// description, which can ask for anything).
const SUGGESTED_REQUIRED_FIELDS: Record<string, string[]> = {
  gov_id: ['id_number', 'name', 'dob', 'address'],
  document: ['bank_name', 'account_number', 'ifsc_code', 'invoice_number', 'total_amount', 'date'],
};

/**
 * Verification settings for media tasks.
 *
 * Rendered only for photo/document/media task types — TaskModal decides. Everything
 * below the master switch stays disabled until verification is turned on, so a
 * half-filled config cannot be saved and then wondered about later.
 */
export const ExtractionConfigSection = ({ control, setValue }: ExtractionConfigSectionProps) => {
  const config = useWatch({ control, name: 'extractionConfig' }) as ExtractionConfig | undefined;
  const [fieldDraft, setFieldDraft] = useState('');

  const enabled = config?.enabled ?? false;
  // Absent means gating is on: the backend defaults gate to true, and a verification
  // that does not block completion is the surprising case, not the default one.
  const gating = config?.gate ?? true;
  const requiredFields = config?.required_fields ?? [];
  const optionalFields = config?.optional_fields ?? [];
  // face_present asks a different question ("is a face visible") than the document
  // checks — there's nothing to transcribe, so "Must be readable" doesn't apply and
  // the backend ignores required_fields/optional_fields for this parser type entirely.
  const isFacePresent = config?.parser_type === 'face_present';
  const isGovID = (config?.parser_type ?? 'gov_id') === 'gov_id';
  const suggestedFields = SUGGESTED_REQUIRED_FIELDS[config?.parser_type ?? 'gov_id'] ?? [];
  // Anything a list holds that isn't one of the suggested checkboxes — either typed in
  // below, or set on an existing task before this parser's suggestions existed.
  const customRequiredFields = requiredFields.filter((field) => !suggestedFields.includes(field));
  const customOptionalFields = optionalFields.filter((field) => !suggestedFields.includes(field));
  const [draftMode, setDraftMode] = useState<'required' | 'optional'>('required');

  const removeField = (list: 'required' | 'optional', name: string) => {
    const key = list === 'required' ? 'extractionConfig.required_fields' : 'extractionConfig.optional_fields';
    const current = list === 'required' ? requiredFields : optionalFields;
    setValue(key, current.filter((field) => field !== name), { shouldDirty: true });
  };

  // A field can only be in one list at a time — moving it to "Required" drops it from
  // "Optional" and vice versa, since "must be present" and "extract if present" are
  // mutually exclusive states for the same field name.
  const setFieldMode = (name: string, mode: 'required' | 'optional' | 'off') => {
    const nextRequired = requiredFields.filter((field) => field !== name);
    const nextOptional = optionalFields.filter((field) => field !== name);
    if (mode === 'required') nextRequired.push(name);
    if (mode === 'optional') nextOptional.push(name);
    setValue('extractionConfig.required_fields', nextRequired, { shouldDirty: true });
    setValue('extractionConfig.optional_fields', nextOptional, { shouldDirty: true });
  };

  const fieldModeOf = (name: string): 'required' | 'optional' | 'off' => {
    if (requiredFields.includes(name)) return 'required';
    if (optionalFields.includes(name)) return 'optional';
    return 'off';
  };

  const addDraftField = () => {
    const name = fieldDraft.trim().toLowerCase().replace(/\s+/g, '_');
    setFieldDraft('');
    if (!name || requiredFields.includes(name) || optionalFields.includes(name)) return;
    setFieldMode(name, draftMode);
  };

  const onFieldKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addDraftField();
    }
  };

  return (
    <>
      <Divider />
      <Box>
        <VStack align="stretch" spacing={4}>
          <FormLabel color="text-primary" fontSize="md" fontWeight="medium" mb={0}>
            Document Verification
          </FormLabel>
          <Text fontSize="sm" color="text-secondary">
            Reads the uploaded file and rejects it if it is the wrong kind of document or
            too unclear to read. The person is asked to upload again.
          </Text>

          <Box bg="bg-surface" p={4} rounded="md" borderWidth="1px" borderColor="border-default">
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <Checkbox
                  isChecked={enabled}
                  onChange={(e) => {
                    const isEnabled = e.target.checked;
                    setValue('extractionConfig.enabled', isEnabled, { shouldDirty: true });
                    // Default "give up after" to 3 the first time verification is turned on,
                    // so a task doesn't accidentally ship with unlimited retries by omission.
                    // Only when unset — re-enabling a task that already chose unlimited (or
                    // some other number) leaves that choice alone.
                    if (isEnabled && config?.max_attempts === undefined) {
                      setValue('extractionConfig.max_attempts', 3, { shouldDirty: true });
                    }
                  }}
                  colorScheme="brand"
                  borderColor="border-muted"
                  sx={{ '[data-checked]': { borderColor: 'brand.500', backgroundColor: 'brand.500' } }}
                  data-testid="wfv4-config-extraction-enabled"
                >
                  Verify uploads for this task
                </Checkbox>
                <FormHelperText fontSize="xs" color="text-muted">
                  Off by default. Existing tasks are unaffected until this is switched on.
                </FormHelperText>
              </FormControl>

              {enabled && (
                <>
                  <FormControl>
                    <FormLabel fontSize="sm" color="text-secondary">
                      What should this be?
                    </FormLabel>
                    <Select
                      size="sm"
                      bg="bg-surface"
                      value={config?.parser_type ?? 'gov_id'}
                      onChange={(e) => {
                        const val = e.target.value as ExtractionConfig['parser_type'];
                        setValue('extractionConfig.parser_type', val, { shouldDirty: true });
                        if (val !== 'gov_id') {
                          setValue('extractionConfig.gov_id_type', undefined, { shouldDirty: true });
                        }
                      }}
                      data-testid="wfv4-config-extraction-parser-type"
                    >
                      <option value="gov_id">Government ID (passport, licence, national ID)</option>
                      <option value="document">Match the task description</option>
                      <option value="face_present">Face photo (selfie / identity photo)</option>
                    </Select>
                    <FormHelperText fontSize="xs" color="text-muted">
                      {isFacePresent
                        ? 'Only checks that a legible human face is visible — not liveness or anti-spoofing, and it does not read any text.'
                        : '"Match the task description" checks the upload against this task\'s own description, so write it as what you expect — e.g. "a signed delivery note".'}
                    </FormHelperText>
                  </FormControl>

                  {isGovID && (
                    <FormControl>
                      <FormLabel fontSize="sm" color="text-secondary">
                        Which ID is this?
                      </FormLabel>
                      <Select
                        placeholder="Any government ID"
                        size="sm"
                        bg="bg-surface"
                        value={config?.gov_id_type ?? ''}
                        onChange={(e) =>
                          setValue(
                            'extractionConfig.gov_id_type',
                            (e.target.value || undefined) as ExtractionConfig['gov_id_type'],
                            { shouldDirty: true },
                          )
                        }
                        data-testid="wfv4-config-extraction-gov-id-type"
                      >
                        <option value="aadhaar">Aadhaar</option>
                        <option value="pan">PAN</option>
                        <option value="passport">Passport</option>
                        <option value="driving_licence">Driving licence</option>
                        <option value="voter_id">Voter ID</option>
                      </Select>
                      <FormHelperText fontSize="xs" color="text-muted">
                        Checks the ID number against this document&apos;s known length/format, e.g. an
                        Aadhaar number must be 12 digits — this is what catches the wrong document being
                        uploaded (a driving licence for an Aadhaar task) even when its number was read
                        correctly. Leave as &quot;Any government ID&quot; to skip this check.
                      </FormHelperText>
                    </FormControl>
                  )}

                  {!isFacePresent && (
                    <FormControl>
                      <FormLabel fontSize="sm" color="text-secondary">
                        Fields to read
                      </FormLabel>
                      {suggestedFields.length > 0 && (
                        <VStack align="stretch" spacing={1} mb={2}>
                          {suggestedFields.map((field) => (
                            <HStack key={field} justify="space-between">
                              <Text fontSize="sm">{field}</Text>
                              <Select
                                size="xs"
                                w="150px"
                                bg="bg-surface"
                                value={fieldModeOf(field)}
                                onChange={(e) => setFieldMode(field, e.target.value as 'required' | 'optional' | 'off')}
                                data-testid={`wfv4-config-extraction-field-${field}`}
                              >
                                <option value="off">Not read</option>
                                <option value="required">Required</option>
                                <option value="optional">Optional</option>
                              </Select>
                            </HStack>
                          ))}
                        </VStack>
                      )}
                      <HStack>
                        <Input
                          size="sm"
                          bg="bg-surface"
                          placeholder="Other field, not listed above — press Enter to add"
                          value={fieldDraft}
                          onChange={(e) => setFieldDraft(e.target.value)}
                          onKeyDown={onFieldKeyDown}
                          onBlur={addDraftField}
                          data-testid="wfv4-config-extraction-field-input"
                        />
                        <Select
                          size="sm"
                          w="130px"
                          bg="bg-surface"
                          value={draftMode}
                          onChange={(e) => setDraftMode(e.target.value as 'required' | 'optional')}
                          data-testid="wfv4-config-extraction-field-draft-mode"
                        >
                          <option value="required">Required</option>
                          <option value="optional">Optional</option>
                        </Select>
                      </HStack>
                      {(customRequiredFields.length > 0 || customOptionalFields.length > 0) && (
                        <Wrap mt={2} spacing={2}>
                          {customRequiredFields.map((field) => (
                            <WrapItem key={`required-${field}`}>
                              <Tag size="sm" colorScheme="brand" borderRadius="full">
                                <TagLabel>{field} (required)</TagLabel>
                                <TagCloseButton onClick={() => removeField('required', field)} />
                              </Tag>
                            </WrapItem>
                          ))}
                          {customOptionalFields.map((field) => (
                            <WrapItem key={`optional-${field}`}>
                              <Tag size="sm" colorScheme="gray" borderRadius="full">
                                <TagLabel>{field} (optional)</TagLabel>
                                <TagCloseButton onClick={() => removeField('optional', field)} />
                              </Tag>
                            </WrapItem>
                          ))}
                        </Wrap>
                      )}
                      <FormHelperText fontSize="xs" color="text-muted">
                        <b>Required</b> fields must be found and readable or the upload is rejected — the
                        strongest check, a blurred ID fails here even when it&apos;s a real ID.{' '}
                        <b>Optional</b> fields are extracted when present but never block the upload — use
                        this for fields that some valid documents legitimately don&apos;t carry, like an
                        address on a gov ID.
                      </FormHelperText>
                    </FormControl>
                  )}

                  <FormControl>
                    <FormLabel fontSize="sm" color="text-secondary">
                      Message when rejected
                    </FormLabel>
                    <Input
                      size="sm"
                      bg="bg-surface"
                      placeholder="Leave blank to use the specific reason it failed"
                      value={config?.failure_message ?? ''}
                      onChange={(e) => setValue('extractionConfig.failure_message', e.target.value, { shouldDirty: true })}
                      data-testid="wfv4-config-extraction-failure-message"
                    />
                    <FormHelperText fontSize="xs" color="text-muted">
                      Blank is usually better — the person is then told exactly what was wrong, such as
                      &quot;the photo is too dark to read the number&quot;.
                    </FormHelperText>
                  </FormControl>

                  <FormControl>
                    <Checkbox
                      isChecked={gating}
                      onChange={(e) => setValue('extractionConfig.gate', e.target.checked, { shouldDirty: true })}
                      colorScheme="brand"
                      borderColor="border-muted"
                      sx={{ '[data-checked]': { borderColor: 'brand.500', backgroundColor: 'brand.500' } }}
                      data-testid="wfv4-config-extraction-gate"
                    >
                      Block the task when verification fails
                    </Checkbox>
                    <FormHelperText fontSize="xs" color="text-muted">
                      Turn this off to trial verification without affecting anyone: uploads are checked
                      and the results recorded, but tasks complete either way.
                    </FormHelperText>
                  </FormControl>

                  <FormControl>
                    <Checkbox
                      isChecked={config?.store_result ?? false}
                      onChange={(e) => setValue('extractionConfig.store_result', e.target.checked, { shouldDirty: true })}
                      colorScheme="brand"
                      borderColor="border-muted"
                      sx={{ '[data-checked]': { borderColor: 'brand.500', backgroundColor: 'brand.500' } }}
                      data-testid="wfv4-config-extraction-store-result"
                    >
                      Save the extracted text
                    </Checkbox>
                    <FormHelperText fontSize="xs" color="text-muted">
                      Off by default. Leave off unless the values are needed later — for identity
                      checks the answer is usually no, and not storing them is one less thing to hold.
                    </FormHelperText>
                  </FormControl>

                  <HStack align="start" spacing={3}>
                    <FormControl maxW="180px">
                      <FormLabel fontSize="sm" color="text-secondary">
                        Minimum confidence
                      </FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        step="0.05"
                        min={0}
                        max={1}
                        bg="bg-surface"
                        placeholder="off"
                        value={config?.min_confidence ?? ''}
                        onChange={(e) =>
                          setValue(
                            'extractionConfig.min_confidence',
                            e.target.value === '' ? undefined : Number(e.target.value),
                            { shouldDirty: true },
                          )
                        }
                        data-testid="wfv4-config-extraction-min-confidence"
                      />
                    </FormControl>
                    {/* Not a FormHelperText: it sits outside the FormControl above (this text
                    applies contextually to the whole row, not just the input), and Chakra's
                    FormHelperText throws at runtime ("useFormControlStyles returned is
                    'undefined'") when rendered without a FormControl ancestor. */}
                    <Text fontSize="xs" color="text-muted" pt={7}>
                      Leave blank. Confidence scores are self-reported and unreliable — a model that has
                      misread a document is often confident about it. Use &quot;Must be readable&quot; instead.
                    </Text>
                  </HStack>

                  <HStack align="start" spacing={3}>
                    <FormControl maxW="180px">
                      <FormLabel fontSize="sm" color="text-secondary">
                        Give up after
                      </FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        step="1"
                        min={1}
                        bg="bg-surface"
                        placeholder="unlimited"
                        value={config?.max_attempts ?? ''}
                        onChange={(e) =>
                          setValue(
                            'extractionConfig.max_attempts',
                            e.target.value === '' ? undefined : Number(e.target.value),
                            { shouldDirty: true },
                          )
                        }
                        data-testid="wfv4-config-extraction-max-attempts"
                      />
                    </FormControl>
                    <Text fontSize="xs" color="text-muted" pt={7}>
                      Leave blank for unlimited retries. After this many rejections, the upload is
                      accepted anyway rather than stranding the person in a loop they can&apos;t escape.
                    </Text>
                  </HStack>

                  {!gating && (
                    <Alert status="info" fontSize="xs" rounded="md">
                      <AlertIcon />
                      Trial mode: nothing is blocked. Check the verification metrics before turning
                      blocking on.
                    </Alert>
                  )}

                  <Alert status="info" fontSize="xs" rounded="md" data-testid="wfv4-config-extraction-pdf-note">
                    <AlertIcon />
                    PDFs are verified the same way as photos. A password-protected PDF, or one too large
                    to read, is rejected with a message saying why.
                  </Alert>
                </>
              )}
            </VStack>
          </Box>
        </VStack>
      </Box>
    </>
  );
};
